import axiosInstance from "./axiosInstance";

/**
 * List all export templates for a project.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export const listTemplates = async (projectId) => {
  const response = await axiosInstance.get(`/projects/${projectId}/templates`);
  return Array.isArray(response.data) ? response.data : [];
};

/**
 * Get a presigned URL for uploading a template file.
 * @param {string} projectId
 * @param {{ file_name: string, name: string }} payload
 * @returns {Promise<{ template_id: string, presigned_url: string, content_type?: string }>}
 */
export const getPresignedUrl = async (projectId, { file_name, name }) => {
  const response = await axiosInstance.post(
    `/projects/${projectId}/template/presigned-url`,
    { file_name, name }
  );
  return response.data;
};

/**
 * Upload a file to a presigned S3 URL.
 * @param {string} presignedUrl
 * @param {File} file
 * @param {string} [contentType]
 * @returns {Promise<void>}
 */
export const uploadFileToPresignedUrl = async (presignedUrl, file, contentType) => {
  const ct = contentType || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener("load", () => {
      if (xhr.status === 200 || xhr.status === 204) resolve();
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    });
    xhr.addEventListener("error", () => reject(new Error("Upload failed")));
    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", ct);
    xhr.send(file);
  });
};

/**
 * Confirm that a template file was uploaded and trigger processing.
 * @param {string} projectId
 * @param {string} templateId
 * @returns {Promise<{ message: string }>}
 */
export const confirmTemplateUpload = async (projectId, templateId) => {
  const response = await axiosInstance.post(
    `/projects/${projectId}/excel-template/confirm-upload`,
    { template_id: templateId }
  );
  return response.data;
};

/**
 * Update (rename) a template.
 * @param {string} projectId
 * @param {string} templateId
 * @param {{ name: string }} payload
 * @returns {Promise<object>}
 */
export const updateTemplate = async (projectId, templateId, { name }) => {
  const response = await axiosInstance.put(
    `/projects/${projectId}/templates/${templateId}`,
    { name }
  );
  return response.data;
};

/**
 * Delete a template (mark inactive).
 * @param {string} projectId
 * @param {string} templateId
 * @returns {Promise<{ message: string }>}
 */
export const deleteTemplate = async (projectId, templateId) => {
  const response = await axiosInstance.delete(
    `/projects/${projectId}/templates/${templateId}`
  );
  return response.data;
};

/**
 * Export project data using a template. Generates Excel and returns a presigned download URL.
 * @param {string} projectId
 * @param {string} templateId
 * @returns {Promise<{ presigned_url: string, expires_in: number, file_name: string }>}
 */
export const exportWithTemplate = async (projectId, templateId) => {
  const response = await axiosInstance.post(`/projects/${projectId}/export`, {
    template_id: templateId,
  });
  return response.data;
};

/**
 * Helper to extract error message from API error (including blob responses).
 * @param {Error} error
 * @returns {Promise<string>}
 */
export const getExportErrorMessage = async (error) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed.detail || "Export not available yet.";
    } catch {
      return "Export not available yet.";
    }
  }
  if (data?.detail) {
    return typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
  }
  return error.message || "Request failed.";
};
